import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/uniqueId';
import { zIndex, boxShadow } from 'styled-system';
import Ref from '../Ref';
import Box from '../Box';
import { useAppearanceFocus, useEscapeHandler } from '../hooks';
import { canUseDOM } from '../utils/environment';
import { H2 } from '../Heading';
import { useThemeDefaults } from '../utils';
import { Fade } from '../animations';
import { OpenState } from '../OpenState';
import { PreventScrolling } from '../utils/preventScrolling';
export var ModalContext = /*#__PURE__*/React.createContext(null);

var generateHeaderId = function generateHeaderId(num) {
  return "modal-header-".concat(num);
};

var ModalBackdrop = useThemeDefaults(function (_ref) {
  var theme = _ref.theme;
  return {
    backgroundColor: theme.modal.backdrop.bg,
    right: 0,
    left: 0,
    bottom: 0,
    top: 0,
    position: 'fixed'
  };
}, Box);
var ModalContent = useThemeDefaults(function (_ref2) {
  var theme = _ref2.theme;
  return {
    paddingTop: theme.modal.content.padding.top
  };
}, Box);
var ModalBox = useThemeDefaults(function (_ref3) {
  var theme = _ref3.theme,
      fullscreen = _ref3.fullscreen,
      modalMaxWidth = _ref3.modalMaxWidth;
  return {
    position: 'relative',
    zIndex: 1,
    margin: 'auto',
    backgroundColor: theme.modal.bg,
    width: fullscreen ? '100%' : 'auto',
    minHeight: fullscreen ? '100%' : 'auto',
    maxWidth: modalMaxWidth ? modalMaxWidth : fullscreen ? '100%' : theme.modal.maxWidth,
    borderRadius: fullscreen ? '0px' : theme.radius
  };
}, Box);
var ModalDetachedWindow = useThemeDefaults(function (_ref4) {
  var theme = _ref4.theme;
  return {
    backgroundColor: theme.modal.bg,
    borderRadius: theme.radius,
    boxShadow: '0 1px 5px 0 rgba(0, 0, 0, 0.05)'
  };
}, styled(Box).withConfig({
  displayName: "Modal__ModalDetachedWindow",
  componentId: "sc-sqaabu-0"
})(boxShadow));
var Header = useThemeDefaults(function (_ref5) {
  var theme = _ref5.theme;
  return {
    paddingLeft: theme.modal.header.padding.left,
    paddingRight: theme.modal.header.padding.right,
    paddingBottom: theme.modal.header.padding.bottom,
    mb: theme.modal.header.margin.bottom,
    mt: theme.modal.header.margin.top,
    textStyle: theme.modal.header.textStyle
  };
}, H2);

var ModalHeader = function ModalHeader(props) {
  var modalContext = useContext(ModalContext);
  return /*#__PURE__*/React.createElement(Header, _extends({
    id: generateHeaderId(modalContext ? modalContext.id : null)
  }, props));
};

var ModalLayout = useThemeDefaults(function (_ref6) {
  var fullscreen = _ref6.fullscreen,
      theme = _ref6.theme;
  return {
    padding: fullscreen ? 0 : theme.modal.edgeSpacing
  };
}, styled(Box).withConfig({
  displayName: "Modal__ModalLayout",
  componentId: "sc-sqaabu-1"
})({
  display: 'flex',
  position: 'fixed',
  zIndex: 10,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  overflow: 'auto'
}));
var FadeStyled = styled(Fade).withConfig({
  displayName: "Modal__FadeStyled",
  componentId: "sc-sqaabu-2"
})({
  zIndex: 10,
  position: 'relative'
}, zIndex);
export var MODAL_REASON = {
  DISMISSED: 'DISMISSED',
  CONFIRMED: 'CONFIRMED'
};
export var ModalBody = useThemeDefaults(function (_ref7) {
  var theme = _ref7.theme;
  return {
    position: 'relative',
    paddingLeft: theme.modal.body.padding.left,
    paddingRight: theme.modal.body.padding.right,
    paddingBottom: theme.modal.body.padding.bottom
  };
}, Box);
export var ModalFooter = useThemeDefaults(function (_ref8) {
  var theme = _ref8.theme;
  return {
    position: 'relative',
    paddingLeft: theme.modal.footer.padding.left,
    paddingRight: theme.modal.footer.padding.right,
    paddingBottom: theme.modal.footer.padding.bottom,
    paddingTop: theme.modal.footer.padding.top,
    borderTop: "1px solid ".concat(theme.colors.keyline)
  };
}, Box);

var _ref11 = /*#__PURE__*/React.createElement(PreventScrolling, null);

export var ModalWindow = /*#__PURE__*/React.memo(function (_ref9) {
  var open = _ref9.open,
      children = _ref9.children,
      _ref9$fullscreen = _ref9.fullscreen,
      fullscreen = _ref9$fullscreen === void 0 ? false : _ref9$fullscreen,
      _ref9$closeOnEscape = _ref9.closeOnEscape,
      closeOnEscape = _ref9$closeOnEscape === void 0 ? true : _ref9$closeOnEscape,
      _ref9$closeOnClickOut = _ref9.closeOnClickOutside,
      closeOnClickOutside = _ref9$closeOnClickOut === void 0 ? false : _ref9$closeOnClickOut,
      closeModal = _ref9.closeModal,
      focusableRef = _ref9.focusableRef,
      zIndex = _ref9.zIndex,
      maxWidth = _ref9.maxWidth,
      width = _ref9.width,
      portalContainer = _ref9.portalContainer;
  var backdropRef = useRef(null);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      id = _useState2[0],
      setId = _useState2[1];

  var requestClose = useCallback(function (reason) {
    if (closeModal) {
      closeModal(reason);
    }
  }, [closeModal]);
  var escapeHandler = useCallback(function () {
    if (closeOnEscape) {
      requestClose(MODAL_REASON.DISMISSED);
    }
  }, [requestClose, closeOnEscape]);
  useAppearanceFocus(open, focusableRef);
  useEscapeHandler(canUseDOM() ? window : null, escapeHandler);
  useEffect(function () {
    if (open) {
      setId(uniqueId());
    }
  }, [open]);
  var clickOutsideHandler = useCallback(function (event) {
    if (backdropRef.current === event.target && closeOnClickOutside) {
      requestClose(MODAL_REASON.DISMISSED);
    }
  }, [requestClose, closeOnClickOutside]);
  return canUseDOM() ? /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(Ref, null, function (_ref10) {
    var ref = _ref10.ref,
        setRef = _ref10.setRef;
    return /*#__PURE__*/React.createElement(ModalContext.Provider, {
      value: {
        id: id,
        modalRef: ref
      }
    }, /*#__PURE__*/React.createElement(React.Fragment, null, open && _ref11, /*#__PURE__*/React.createElement(FadeStyled, {
      visible: open,
      zIndex: zIndex
    }, /*#__PURE__*/React.createElement(ModalLayout, {
      fullscreen: fullscreen,
      role: "dialog",
      "aria-modal": true,
      "aria-labelledby": generateHeaderId(id)
    }, /*#__PURE__*/React.createElement(ModalBackdrop, {
      ref: backdropRef,
      onClick: clickOutsideHandler
    }), /*#__PURE__*/React.createElement(ModalBox, {
      ref: setRef,
      modalMaxWidth: maxWidth,
      width: width,
      fullscreen: fullscreen
    }, children)))));
  }), portalContainer || document.body) : null;
});
process.env.NODE_ENV !== "production" ? ModalWindow.propTypes = {
  open: PropTypes.bool.isRequired,
  children: PropTypes.any.isRequired,
  closeOnClickOutside: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  closeModal: PropTypes.func,
  focusableRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(typeof Element === 'undefined' ? function () {} : Element)
  })])
} : void 0;
export var Modal = {};
Modal.Header = ModalHeader;
Modal.Window = ModalWindow;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.State = OpenState;
Modal.Content = ModalContent;
Modal.DetachedWindow = ModalDetachedWindow;
export default Modal;